// src/pages/HomePage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import products from '../data/products';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const featuredProducts = products.filter(p => p.isNew).slice(0, 4);
  const categories = [...new Set(products.map(p => p.category))];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const carouselRef = useRef(null);
  const intervalRef = useRef(null);

  const categoryIcons = {
    'Flower': '🌿',
    'Exotic-Flower': '🌸',
    'Edibles': '🍬',
    'Concentrates': '⚗️',
    'cart': '🔋',
    'disposable-cart': '💨',
    'Pre-rolls': '🚬',
    'Shake': '🍃'
  };

  const categoryColors = {
    'Flower': '#4CAF50',
    'Exotic-Flower': '#9C27B0',
    'Edibles': '#FF9800',
    'Concentrates': '#673AB7',
    'cart': '#2196F3',
    'disposable-cart': '#00BCD4',
    'Pre-rolls': '#F44336',
    'Shake': '#795548'
  };

  // Function to handle navigation with scroll to top
  const handleLinkClick = (path) => {
    navigate(path);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Function for hero buttons
  const handleHeroButtonClick = (path) => {
    navigate(path);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Function for category cards
  const handleCategoryClick = (category) => {
    navigate(`/category/${category}`);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const slidesPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1200) return 4;
      if (window.innerWidth >= 768) return 3;
      if (window.innerWidth >= 480) return 2;
    }
    return 1;
  };

  const totalSlides = Math.ceil(categories.length / slidesPerView());

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    if (isAutoPlaying) {
      intervalRef.current = setInterval(nextSlide, 5000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying, currentSlide]);

  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  const getCategoryStats = (category) => {
    return products.filter(p => p.category === category).length;
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section id="hero" className="hero-section"
        style={{
          backgroundImage: 'url("/images/heros.PNG")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          boxShadow: '0 6px 25px rgba(38, 4, 77, 0.4)'
        }}
      >
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 18, 7, 0.29)',
          zIndex: 1
        }}></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">Washington DC's Trusted Source</div>
            <h1 className="hero-title">
              Premium Cannabis
              <span className="hero-title-accent">Delivery</span>
            </h1>
            <p className="hero-subtitle">
              Experience the finest selection of lab-tested cannabis products.
              Fast, discreet, and professional delivery right to your door.
            </p>
            <div className="hero-actions">
              <button
                onClick={() => handleHeroButtonClick('/products')}
                className="btn btn-outline btn-lg"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '1rem',
                  fontWeight: '600',
                  padding: 'var(--space-md) var(--space-xl)',
                  border: '2px solid white',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'transparent',
                  color: 'white',
                  transition: 'all 0.2s ease',
                  textDecoration: 'none',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = 'var(--primary)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'white';
                }}
              >
                <span className="btn-icon">🛍️</span>
                Shop All Products
              </button>
              <button
                onClick={() => handleCategoryClick('Deals')}
                className="btn btn-outline btn-lg"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '1rem',
                  fontWeight: '600',
                  padding: 'var(--space-md) var(--space-xl)',
                  border: '2px solid white',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'transparent',
                  color: 'white',
                  transition: 'all 0.2s ease',
                  textDecoration: 'none',
                  textAlign: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = 'var(--primary)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'white';
                }}
              >
                <span className="btn-icon">🔥</span>
                Weekly Specials
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="section featured-section">
        <div id="featured" className="container">
          <div className="section-header">
            <div className="header-content">
              <h2 className="section-title">Featured Products</h2>
              <p className="section-subtitle">New arrivals & customer favorites</p>
            </div>
            <button
              onClick={() => handleLinkClick('/products')}
              className="btn btn-outline"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: '1rem',
                fontWeight: '600',
                padding: 'var(--space-md) var(--space-lg)',
                border: '2px solid var(--primary)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'transparent',
                color: 'var(--primary)',
                transition: 'all 0.2s ease',
                textDecoration: 'none',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'var(--primary)';
                e.target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = 'var(--primary)';
              }}
            >
              View All Products
              <svg className="arrow-icon" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10m-5 5l5-5-5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="products-grid">
            {featuredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Category Carousel */}
      <section className="section carousel-section">
        <div className="container">
          <div className="section-header">
            <div className="header-content">
              <h2 className="section-title">Shop by Category</h2>
              <p className="section-subtitle">Explore our curated collections</p>
            </div>
            <div className="carousel-controls">
              <button
                className="carousel-btn prev"
                onClick={prevSlide}
                aria-label="Previous categories"
                style={{
                  background: 'none',
                  border: '1px solid var(--border)',
                  borderRadius: '50%',
                  width: '2.5rem',
                  height: '2.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-50)';
                  e.target.style.borderColor = 'var(--primary)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = 'var(--border)';
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" style={{ width: '1.25rem', height: '1.25rem' }}>
                  <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
              <button
                className="carousel-btn next"
                onClick={nextSlide}
                aria-label="Next categories"
                style={{
                  background: 'none',
                  border: '1px solid var(--border)',
                  borderRadius: '50%',
                  width: '2.5rem',
                  height: '2.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--gray-50)';
                  e.target.style.borderColor = 'var(--primary)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = 'var(--border)';
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" style={{ width: '1.25rem', height: '1.25rem' }}>
                  <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>

          <div
            className="category-carousel"
            ref={carouselRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div
              className="carousel-track"
              style={{
                transform: `translateX(-${currentSlide * 100}%)`,
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div key={slideIndex} className="carousel-slide">
                  {categories
                    .slice(slideIndex * slidesPerView(), (slideIndex + 1) * slidesPerView())
                    .map(category => {
                      const productCount = getCategoryStats(category);
                      const icon = categoryIcons[category] || '📦';
                      const color = categoryColors[category] || '#666';

                      return (
                        <button
                          key={category}
                          onClick={() => handleCategoryClick(category)}
                          className="category-card-carousel"
                          style={{ 
                            '--category-color': color,
                            cursor: 'pointer',
                            border: 'none',
                            background: 'white',
                            borderRadius: 'var(--radius-lg)',
                            padding: 'var(--space-lg)',
                            boxShadow: 'var(--shadow-sm)',
                            transition: 'all 0.3s ease',
                            position: 'relative',
                            overflow: 'hidden',
                            textAlign: 'left',
                            fontFamily: 'inherit',
                            fontSize: 'inherit'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-8px)';
                            e.target.style.boxShadow = 'var(--shadow-lg)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'var(--shadow-sm)';
                          }}
                        >
                          <div className="card-header-carousel">
                            <div className="category-icon-carousel">
                              {icon}
                            </div>
                            <div className="category-arrow">
                              <svg viewBox="0 0 24 24" fill="none">
                                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                              </svg>
                            </div>
                          </div>

                          <div className="card-content-carousel">
                            <h3 className="category-name-carousel">
                              {category.replace('-', ' ')}
                            </h3>
                            <div className="category-stats-carousel">
                              <span className="stat-badge">
                                <svg viewBox="0 0 24 24" fill="none" className="stat-icon">
                                  <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                                {productCount} products
                              </span>
                            </div>
                          </div>

                          <div className="card-footer-carousel">
                            <span className="explore-text">Explore</span>
                          </div>

                          <div className="card-hover-effect"></div>
                        </button>
                      );
                    })}
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Dots */}
          <div className="carousel-dots">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                style={{
                  width: '0.75rem',
                  height: '0.75rem',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: index === currentSlide ? 'var(--primary)' : 'var(--border)',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (index !== currentSlide) {
                    e.target.style.backgroundColor = 'var(--primary-light)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (index !== currentSlide) {
                    e.target.style.backgroundColor = 'var(--border)';
                  }
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section benefits-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose ZA420 DC</h2>
            <p className="section-subtitle">Experience the difference with premium service</p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
                    stroke="currentColor" strokeWidth="1.5" />
                  <path d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1h1a1 1 0 001-1v-3h1a2 2 0 012 2v2a1 1 0 001 1h1"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <h3>Fast Delivery</h3>
              <p>Delivery within 1 hour across Washington DC with real-time tracking</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 15v3m0 3h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.694-.833-2.464 0L4.346 19.5c-.77.833.192 2.5 1.732 2.5z"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <h3>Premium Quality</h3>
              <p>Lab-tested products from trusted suppliers with verified potency</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <h3>Secure & Discreet</h3>
              <p>Professional delivery with vehicles and discreet packaging</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;